import { useState, useEffect, useCallback } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, useParams, useNavigate } from '@tanstack/react-router'
import type { UIMessage } from 'ai'
import { TiptapAiEditor } from '@/tiptap/editor/ai-editor'
import { deserializeFromMarkdown } from '@/tiptap/markdown/deserializeFromMarkdown'
import {
  FileText,
  Loader2,
  CheckCircle2,
  X,
  ChevronRight,
  SkipForward,
  Play,
  ArrowRight,
  Check,
  ChevronLeft,
} from 'lucide-react'
import { trpc } from '@/lib/trpc'
import { apiEndpoints } from '@/lib/env'
import { ChatPanel } from '@/components/chat/ChatPanel'
import { ScreenataOnboardingHeader } from '@/components/screenata/ScreenataOnboardingHeader'

export const Route = createFileRoute(
  '/(protected)/workspaces/$workspaceId/screenata/generate-policies',
)({
  component: GeneratePoliciesPage,
})

// ── Types ──

type GenerationItem = {
  slug: string
  title: string
  controlRefs: string[]
  priority: number
  category: 'policy' | 'deliverable'
  necessity: 'required' | 'common-ask' | 'scope-dependent' | 'optional'
  necessityReason: string | null
  evidenceEffort: 'low' | 'medium' | 'high' | null
  isGenerated: boolean
  claimCount: number
  docId: string | null
  templateId: string | null
}

type PolicyStatus =
  | 'not-started'
  | 'generating'
  | 'generated'
  | 'skipped'

// ── Helpers ──

function derivePolicyStatus(
  item: GenerationItem,
  skippedSlugs: Set<string>,
): PolicyStatus {
  if (skippedSlugs.has(item.slug)) return 'skipped'
  if (!item.isGenerated) return 'not-started'
  return 'generated'
}

// ── Badge Components ──

function PolicyStatusBadge({ status }: { status: PolicyStatus }) {
  switch (status) {
    case 'generated':
      return (
        <span className='inline-flex items-center gap-1 rounded bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-700'>
          <CheckCircle2 className='h-3 w-3' />
          Generated
        </span>
      )
    case 'generating':
      return (
        <span className='inline-flex items-center gap-1 rounded bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-600'>
          <Loader2 className='h-3 w-3 animate-spin' />
          Generating
        </span>
      )
    case 'skipped':
      return (
        <span className='inline-flex items-center rounded bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-500'>
          Skipped
        </span>
      )
    default:
      return (
        <span className='inline-flex items-center rounded bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600'>
          Not Started
        </span>
      )
  }
}

function NecessityBadge({
  necessity,
  reason,
}: {
  necessity: string
  reason?: string | null
}) {
  if (necessity === 'required') return null
  if (necessity === 'common-ask') {
    return (
      <span className='shrink-0 rounded border border-blue-200 bg-blue-50 px-1.5 py-0.5 text-xs text-blue-700'>
        Common Ask
      </span>
    )
  }
  if (necessity === 'scope-dependent') {
    return (
      <span
        className='shrink-0 rounded border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-xs text-amber-700'
        title={reason ?? 'Depends on your scope'}
      >
        Scope-dependent
      </span>
    )
  }
  return (
    <span
      className='shrink-0 rounded border border-gray-200 bg-gray-100 px-1.5 py-0.5 text-xs text-gray-600'
      title={reason ?? 'Optional'}
    >
      Optional
    </span>
  )
}

// ── Main Page ──

function GeneratePoliciesPage() {
  const { workspaceId } = useParams({
    from: '/(protected)/workspaces/$workspaceId/screenata/generate-policies',
  })
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const itemsQuery = useQuery(
    trpc.complianceProgram.getGenerationItems.queryOptions({ framework: 'SOC2' }),
  )
  const programQuery = useQuery(
    trpc.complianceProgram.getProgram.queryOptions({ framework: 'SOC2' }),
  )

  const programId = (programQuery.data as any)?.id ?? ''

  const items: GenerationItem[] = ((itemsQuery.data as any)?.items ?? []) as GenerationItem[]

  // Skipped slugs from program
  const skippedPolicies: Array<{ slug: string; reason: string }> =
    ((programQuery.data as any)?.skippedPolicies ?? []) as Array<{
      slug: string
      reason: string
    }>
  const skippedSlugs = new Set(skippedPolicies.map((s) => s.slug))

  // Sort all items by priority
  const sortedItems = [...items].sort((a, b) => a.priority - b.priority)

  // Stats
  const generatedCount = items.filter((i) => i.isGenerated).length
  const skippedCount = items.filter((i) => skippedSlugs.has(i.slug)).length
  const actionableCount = items.length - skippedCount
  const allDone = actionableCount > 0 && generatedCount >= actionableCount

  const [viewingItem, setViewingItem] = useState<GenerationItem | null>(null)

  if (itemsQuery.isLoading || programQuery.isLoading) {
    return (
      <div className='flex min-h-screen flex-col bg-background'>
        <ScreenataOnboardingHeader />
        <div className='flex flex-1 items-center justify-center'>
          <Loader2 className='h-5 w-5 animate-spin text-muted-foreground' />
        </div>
      </div>
    )
  }

  if (!programId) {
    return (
      <div className='flex min-h-screen flex-col bg-background'>
        <ScreenataOnboardingHeader />
        <div className='flex flex-1 items-center justify-center text-muted-foreground'>
          No compliance program found. Start from the setup flow.
        </div>
      </div>
    )
  }

  return (
    <div className='flex min-h-screen flex-col bg-background'>
      <ScreenataOnboardingHeader />

      <main className='flex-1 overflow-y-auto'>
        <div className='mx-auto w-full max-w-4xl space-y-6 px-6 py-10'>
          {/* Section label */}
          <div className='text-xs font-semibold uppercase tracking-widest text-primary/70'>
            Policies
          </div>

          {/* Title */}
          <div>
            <h2 className='text-2xl font-semibold leading-snug text-foreground font-anek'>
              Generate your policies
            </h2>
            <p className='mt-2 text-base text-muted-foreground leading-relaxed'>
              Click each policy to answer a few questions and generate a tailored document.
              {actionableCount > 0 && (
                <span className='ml-1 font-medium text-foreground'>
                  {generatedCount} of {actionableCount} generated.
                </span>
              )}
            </p>
          </div>

          {/* Progress bar */}
          {actionableCount > 0 && (
            <div className='space-y-1.5'>
              <div className='flex h-2 w-full overflow-hidden rounded-full bg-gray-100'>
                <div
                  className='h-2 bg-emerald-500 transition-all duration-500'
                  style={{ width: `${(generatedCount / actionableCount) * 100}%` }}
                />
              </div>
            </div>
          )}

          {/* Gate banner */}
          {allDone && (
            <div className='flex items-center justify-between gap-3 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3'>
              <div className='flex items-center gap-2'>
                <CheckCircle2 className='h-4 w-4 shrink-0 text-emerald-600' />
                <p className='text-sm font-medium text-emerald-800'>
                  All policies generated — you&apos;re ready to continue.
                </p>
              </div>
              <button
                onClick={() =>
                  navigate({
                    to: '/workspaces/$workspaceId/screenata/readiness',
                    params: { workspaceId },
                  })
                }
                className='inline-flex shrink-0 items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-emerald-700'
              >
                Continue to Readiness
                <ArrowRight className='h-3.5 w-3.5' />
              </button>
            </div>
          )}

          {/* Policy list */}
          {sortedItems.length === 0 ? (
            <div className='text-muted-foreground rounded-xl border border-dashed p-8 text-center text-sm'>
              No policies found.
            </div>
          ) : (
            <div className='bg-card overflow-hidden rounded-xl border'>
              {sortedItems.map((item, idx) => {
                const status = derivePolicyStatus(item, skippedSlugs)
                return (
                  <div
                    key={item.slug}
                    className={`border-border/80 ${idx < sortedItems.length - 1 ? 'border-b' : ''}`}
                  >
                    <PolicyCard
                      item={item}
                      status={status}
                      programId={programId}
                      skippedSlugs={skippedSlugs}
                      onOpen={() => setViewingItem(item)}
                    />
                  </div>
                )
              })}
            </div>
          )}

          {/* Footer navigation */}
          <div className='flex items-center gap-4 pt-4'>
            {allDone && (
              <button
                onClick={() =>
                  navigate({
                    to: '/workspaces/$workspaceId/screenata/readiness',
                    params: { workspaceId },
                  })
                }
                className='inline-flex items-center gap-2 rounded-lg bg-foreground px-5 py-2.5 text-sm font-medium text-background transition-opacity hover:opacity-90'
              >
                Continue
                <ArrowRight className='h-4 w-4' />
              </button>
            )}
          </div>
        </div>
      </main>

      {/* Fixed back/forward arrows */}
      <div className='fixed bottom-6 right-6 flex items-center gap-1'>
        <button
          type='button'
          onClick={() =>
            navigate({
              to: '/workspaces/$workspaceId/screenata/scope-confirm',
              params: { workspaceId },
            })
          }
          className='flex h-9 w-9 items-center justify-center rounded-lg border border-border bg-background text-foreground transition-colors hover:bg-muted'
          aria-label='Previous'
        >
          <ChevronLeft className='h-4 w-4' />
        </button>
        <button
          type='button'
          onClick={
            allDone
              ? () =>
                  navigate({
                    to: '/workspaces/$workspaceId/screenata/readiness',
                    params: { workspaceId },
                  })
              : undefined
          }
          disabled={!allDone}
          className={`flex h-9 w-9 items-center justify-center rounded-lg border border-border transition-colors ${
            !allDone
              ? 'cursor-not-allowed opacity-30'
              : 'bg-background text-foreground hover:bg-muted'
          }`}
          aria-label='Next'
        >
          <ChevronRight className='h-4 w-4' />
        </button>
      </div>

      {/* Policy split pane (chat + editor) */}
      {viewingItem && (
        <PolicySplitPane
          item={viewingItem}
          workspaceId={workspaceId}
          programId={programId}
          onClose={() => {
            setViewingItem(null)
            // Refresh data after closing to pick up newly generated policies
            queryClient.invalidateQueries({
              queryKey: trpc.complianceProgram.getGenerationItems.queryKey({ framework: 'SOC2' }),
            })
          }}
        />
      )}
    </div>
  )
}

// ── Policy Card ──

function PolicyCard({
  item,
  status,
  programId,
  skippedSlugs,
  onOpen,
}: {
  item: GenerationItem
  status: PolicyStatus
  programId: string
  skippedSlugs: Set<string>
  onOpen: () => void
}) {
  const queryClient = useQueryClient()
  const [showSkipInput, setShowSkipInput] = useState(false)
  const [skipReason, setSkipReason] = useState('')

  const skipMutation = useMutation(trpc.claimTriage.skipPolicy.mutationOptions())
  const unskipMutation = useMutation(trpc.claimTriage.unskipPolicy.mutationOptions())

  const isSkipped = skippedSlugs.has(item.slug)

  const invalidate = () => {
    queryClient.invalidateQueries({
      queryKey: trpc.complianceProgram.getProgram.queryKey({ framework: 'SOC2' }),
    })
    queryClient.invalidateQueries({
      queryKey: trpc.complianceProgram.getGenerationItems.queryKey({ framework: 'SOC2' }),
    })
  }

  const handleSkip = () => {
    if (!skipReason.trim()) return
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ;(skipMutation.mutate as any)(
      { programId, slug: item.slug, reason: skipReason },
      {
        onSuccess: () => {
          setShowSkipInput(false)
          setSkipReason('')
          invalidate()
        },
      },
    )
  }

  const handleUnskip = () => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ;(unskipMutation.mutate as any)(
      { programId, slug: item.slug },
      { onSuccess: invalidate },
    )
  }

  return (
    <div
      onClick={!isSkipped ? onOpen : undefined}
      className={`px-4 py-3 transition-colors ${
        !isSkipped ? 'cursor-pointer hover:bg-gray-50' : ''
      } ${isSkipped ? 'opacity-60' : ''}`}
    >
      <div className='flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between'>
        {/* Left: title + badges */}
        <div className='flex min-w-0 flex-1 flex-wrap items-start gap-2'>
          <FileText
            className={`mt-0.5 h-4 w-4 shrink-0 ${
              status === 'generated'
                ? 'text-emerald-600'
                : status === 'not-started' || isSkipped
                  ? 'text-gray-400'
                  : 'text-blue-500'
            }`}
          />
          <h3 className='min-w-0 flex-1 text-sm font-medium leading-snug'>
            {item.title}
          </h3>
          <NecessityBadge necessity={item.necessity} reason={item.necessityReason} />
        </div>

        {/* Right: status + actions */}
        <div
          className='flex shrink-0 flex-wrap items-center gap-1.5'
          onClick={(e) => e.stopPropagation()}
        >
          <PolicyStatusBadge status={status} />

          {/* Actions */}
          {status === 'not-started' && (
            <>
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  onOpen()
                }}
                className='inline-flex items-center gap-1 rounded bg-emerald-600 px-2 py-0.5 text-xs font-medium text-white transition-colors hover:bg-emerald-700'
              >
                <Play className='h-3 w-3' />
                Generate
              </button>
              {!showSkipInput && (
                <button
                  onClick={() => setShowSkipInput(true)}
                  className='inline-flex items-center gap-1 rounded border px-2 py-0.5 text-xs font-medium text-gray-500 transition-colors hover:bg-gray-100'
                >
                  <SkipForward className='h-3 w-3' />
                  Skip
                </button>
              )}
            </>
          )}

          {isSkipped && (
            <button
              onClick={handleUnskip}
              disabled={unskipMutation.isPending}
              className='inline-flex items-center gap-1 rounded border px-2 py-0.5 text-xs font-medium text-gray-500 transition-colors hover:bg-gray-100 disabled:opacity-60'
            >
              Undo Skip
            </button>
          )}

          {item.isGenerated && !isSkipped && (
            <button
              onClick={onOpen}
              className='inline-flex items-center gap-1 rounded bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700 transition-colors hover:bg-blue-100'
            >
              <ChevronRight className='h-3 w-3' />
              View
            </button>
          )}
        </div>
      </div>

      {/* Control refs */}
      <div className='mt-1.5 flex flex-wrap gap-1 pl-6'>
        {item.controlRefs.map((ref) => (
          <span
            key={ref}
            className={`inline-block rounded px-1.5 py-0.5 font-mono text-xs ${
              item.isGenerated
                ? 'border border-emerald-200 bg-emerald-50 text-emerald-700'
                : 'border border-gray-200 bg-gray-50 text-gray-500'
            }`}
          >
            {ref}
          </span>
        ))}
      </div>

      {/* Skip input */}
      {showSkipInput && (
        <div
          className='mt-2 pl-6'
          onClick={(e) => e.stopPropagation()}
        >
          <div className='flex items-center gap-2'>
            <input
              type='text'
              value={skipReason}
              onChange={(e) => setSkipReason(e.target.value)}
              placeholder='Reason for skipping...'
              className='flex-1 rounded border px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-amber-400'
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSkip()
                if (e.key === 'Escape') {
                  setShowSkipInput(false)
                  setSkipReason('')
                }
              }}
              autoFocus
            />
            <button
              onClick={handleSkip}
              disabled={!skipReason.trim() || skipMutation.isPending}
              className='inline-flex items-center gap-1 rounded bg-amber-500 px-2 py-1 text-xs font-medium text-white disabled:opacity-50'
            >
              {skipMutation.isPending ? (
                <Loader2 className='h-3 w-3 animate-spin' />
              ) : (
                <Check className='h-3 w-3' />
              )}
              Confirm
            </button>
            <button
              onClick={() => {
                setShowSkipInput(false)
                setSkipReason('')
              }}
              className='rounded p-1 text-gray-400 hover:bg-gray-100'
            >
              <X className='h-3.5 w-3.5' />
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

// ── Utility: extract docId from generatePolicy tool results in chat messages ──

function extractGeneratedDocId(messages: UIMessage[]): string | null {
  for (let i = messages.length - 1; i >= 0; i--) {
    const msg = messages[i]
    if (msg.role !== 'assistant') continue
    for (const part of (msg.parts ?? []) as any[]) {
      // v6 format: part.type === 'tool-generatePolicy'
      if (part.type === 'tool-generatePolicy' && part.state === 'output-available' && part.output) {
        try {
          const result = typeof part.output === 'string' ? JSON.parse(part.output) : part.output
          if (result?.success && result?.docId) return result.docId
        } catch { /* ignore */ }
      }
      // Legacy format: part.type === 'tool-invocation'
      if (part.type === 'tool-invocation') {
        const ti = part.toolInvocation
        if (!ti || ti.toolName !== 'generatePolicy') continue
        if (ti.state !== 'result') continue
        try {
          const result = typeof ti.result === 'string' ? JSON.parse(ti.result) : ti.result
          if (result?.success && result?.docId) return result.docId
        } catch { /* ignore */ }
      }
    }
  }
  return null
}

// ── Policy Split Pane (Chat + Editor) ──

function PolicySplitPane({
  item,
  workspaceId,
  programId,
  onClose,
}: {
  item: GenerationItem
  workspaceId: string
  programId: string
  onClose: () => void
}) {
  const queryClient = useQueryClient()

  // Track the docId detected from the chat stream
  const [detectedDocId, setDetectedDocId] = useState<string | null>(
    item.isGenerated ? item.docId : null,
  )
  const [initialContent, setInitialContent] = useState<any>(null)
  const [contentReady, setContentReady] = useState(false)

  // Listen for policy-generated event from GeneratePolicyMessage component
  useEffect(() => {
    const handler = (e: Event) => {
      const docId = (e as CustomEvent).detail?.docId
      if (!docId || detectedDocId) return
      setDetectedDocId(docId)
      queryClient.invalidateQueries({
        queryKey: trpc.complianceProgram.getGenerationItems.queryKey({ framework: 'SOC2' }),
      })
    }
    window.addEventListener('screenata:policy-generated', handler)
    return () => window.removeEventListener('screenata:policy-generated', handler)
  }, [detectedDocId, queryClient])

  // Fallback: also scan chat messages for generatePolicy tool results
  const handleMessagesChange = useCallback((messages: UIMessage[]) => {
    if (detectedDocId) return
    const docId = extractGeneratedDocId(messages)
    if (docId) {
      setDetectedDocId(docId)
      queryClient.invalidateQueries({
        queryKey: trpc.complianceProgram.getGenerationItems.queryKey({ framework: 'SOC2' }),
      })
    }
  }, [detectedDocId, queryClient])

  // Fetch doc content when we have a docId
  const docQuery = useQuery({
    ...trpc.complianceProgram.getPolicyDocContent.queryOptions({
      docId: detectedDocId ?? '',
    }),
    enabled: !!detectedDocId,
  })

  // Convert doc content for the editor
  useEffect(() => {
    if (!docQuery.data || contentReady) return
    const docData = docQuery.data as any
    const { plateContent, content } = docData as { plateContent: any; content: string }
    const pcContent = (plateContent as any)?.content
    const isSingleEmptyParagraph =
      Array.isArray(pcContent) &&
      pcContent.length === 1 &&
      pcContent[0]?.type === 'paragraph' &&
      (!pcContent[0]?.content || pcContent[0].content.length === 0)
    const hasPlateContent =
      plateContent &&
      typeof plateContent === 'object' &&
      Array.isArray(pcContent) &&
      pcContent.length > 0 &&
      !isSingleEmptyParagraph

    if (hasPlateContent) {
      setInitialContent(plateContent)
      setContentReady(true)
      return
    }

    if (content && content.length > 10) {
      try {
        const result = deserializeFromMarkdown(content)
        setInitialContent(result.content)
        setContentReady(true)
      } catch {
        setInitialContent({ type: 'doc', content: [] })
        setContentReady(true)
      }
      return
    }

    setInitialContent({ type: 'doc', content: [] })
    setContentReady(true)
  }, [docQuery.data, contentReady])

  const hasContent = contentReady && initialContent?.content?.length > 0
  const showEditor = !!detectedDocId

  return (
    <div className='fixed inset-0 z-50 flex'>
      {/* Backdrop */}
      <div className='absolute inset-0 bg-black/40' onClick={onClose} />

      {/* Split pane container */}
      <div className='relative ml-auto flex h-full w-full max-w-7xl bg-white shadow-xl'>
        {/* Left: Chat panel */}
        <div className={`flex flex-col ${showEditor ? 'w-[420px] shrink-0' : 'flex-1'} border-r border-border`}>
          {/* Chat header */}
          <div className='flex shrink-0 items-center justify-between border-b px-4 py-3'>
            <div className='flex min-w-0 items-center gap-2'>
              <FileText className='h-4 w-4 shrink-0 text-blue-600' />
              <h2 className='truncate text-sm font-semibold'>{item.title}</h2>
            </div>
            <button
              onClick={onClose}
              className='text-muted-foreground rounded-lg p-1 hover:bg-gray-100'
            >
              <X className='h-4 w-4' />
            </button>
          </div>

          {/* ChatPanel */}
          <div className='flex-1 overflow-hidden'>
            <ChatPanel
              api={apiEndpoints.screenataPolicyChat}
              requestBody={{ programId, policySlug: item.slug }}
              sendOnlyLastMessage={false}
              className='border-l-0'
              onMessagesChange={handleMessagesChange}
              hideHeader
              autoStartMessage={!item.isGenerated ? `Let's generate the ${item.title}.` : undefined}
            />
          </div>

          {/* Control refs footer */}
          <div className='shrink-0 border-t bg-gray-50 px-4 py-2'>
            <div className='flex flex-wrap items-center gap-1'>
              <span className='text-muted-foreground text-xs'>Controls:</span>
              {item.controlRefs.map((ref) => (
                <span
                  key={ref}
                  className='inline-block rounded border border-blue-200 bg-blue-50 px-1.5 py-0.5 font-mono text-xs text-blue-700'
                >
                  {ref}
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* Right: Editor pane (appears when policy is generated) */}
        {showEditor && (
          <div className='flex flex-1 flex-col overflow-hidden'>
            {/* Editor header */}
            <div className='flex shrink-0 items-center justify-between border-b px-4 py-3'>
              <span className='text-sm font-medium text-foreground'>Policy Preview</span>
              <span className='inline-flex items-center gap-1 rounded bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-500'>
                Read only
              </span>
            </div>

            {/* Editor content */}
            <div className='flex-1 overflow-y-auto'>
              {(docQuery.isLoading || !contentReady) && (
                <div className='flex items-center justify-center py-12'>
                  <Loader2 className='h-6 w-6 animate-spin text-blue-600' />
                </div>
              )}
              {contentReady && !hasContent && (
                <div className='text-muted-foreground px-6 py-12 text-center'>
                  <p>Document is empty.</p>
                  <p className='mt-1 text-sm'>
                    The policy template exists but has no content yet.
                  </p>
                </div>
              )}
              {contentReady && hasContent && (
                <TiptapAiEditor
                  content={initialContent}
                  readOnly={true}
                  enableComments={false}
                  docId={detectedDocId ?? undefined}
                  workspaceId={workspaceId}
                />
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
